import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { intakeAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import {
  Receipt, Users, Plus, Link as LinkIcon, Mail, Copy, Eye, FileText, RefreshCw,
  Building2, Upload, ExternalLink, FolderOpen, Download, Trash2, ArrowLeft,
  CheckCircle, Clock, AlertCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const DELIVERABLE_LABELS = {
  profit_and_loss: 'Profit & Loss', balance_sheet: 'Balance Sheet',
  trial_balance: 'Trial Balance', financial_statements: 'Financial Statements',
  tax_summaries: 'Tax Summaries', other: 'Other',
};
const YEARS = Array.from({length: 5}, (_, i) => new Date().getFullYear() - i);
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const IntakePage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [clientForm, setClientForm] = useState({ name: '', email: '', phone: '', contact_person: '' });

  const tenantId = user?.tenant_id || 'default';

  const loadClients = useCallback(async () => {
    setLoading(true);
    try { const res = await intakeAPI.listClients({ tenant_id: tenantId }); setClients(res.data); }
    catch { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { loadClients(); }, [loadClients]);

  const handleCreateClient = async () => {
    if (!clientForm.name.trim()) { toast.error('Enter a name'); return; }
    try {
      await intakeAPI.createClient({ tenant_id: tenantId, ...clientForm });
      toast.success('Client created');
      setClientDialogOpen(false);
      setClientForm({ name: '', email: '', phone: '', contact_person: '' });
      loadClients();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openWorkspace = async (client) => {
    setSelectedClient(client);
    try { const { data } = await intakeAPI.getClientWorkspace(client.id); setWorkspace(data); }
    catch { toast.error('Failed to load workspace'); }
  };

  const copyToClipboard = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  // Client workspace view
  if (selectedClient && workspace) {
    return <ClientWorkspace client={selectedClient} workspace={workspace} onBack={() => { setSelectedClient(null); setWorkspace(null); }} tenantId={tenantId} onRefresh={() => openWorkspace(selectedClient)} />;
  }

  return (
    <div className="space-y-6" data-testid="intake-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-semibold text-slate-900">Client Intake</h1><p className="text-slate-500 mt-1">Manage clients and document submissions</p></div>
        <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
          <DialogTrigger asChild><Button data-testid="add-client-btn"><Plus className="mr-2 h-4 w-4" />Add Client</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Client</DialogTitle><DialogDescription>Create a new intake client</DialogDescription></DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2"><Label>Name *</Label><Input placeholder="Company name" value={clientForm.name} onChange={e => setClientForm(p => ({...p, name: e.target.value}))} data-testid="client-name-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={clientForm.email} onChange={e => setClientForm(p => ({...p, email: e.target.value}))} /></div>
                <div className="space-y-2"><Label>Phone</Label><Input value={clientForm.phone} onChange={e => setClientForm(p => ({...p, phone: e.target.value}))} /></div>
              </div>
              <div className="space-y-2"><Label>Contact Person</Label><Input value={clientForm.contact_person} onChange={e => setClientForm(p => ({...p, contact_person: e.target.value}))} /></div>
              <div className="flex justify-end gap-3"><Button variant="outline" onClick={() => setClientDialogOpen(false)}>Cancel</Button><Button onClick={handleCreateClient} data-testid="save-client-btn">Create</Button></div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-52" />)}</div>
      : clients.length === 0 ? <Card><CardContent className="text-center py-16"><Users className="h-12 w-12 text-slate-300 mx-auto mb-4" /><h3 className="text-lg font-medium">No clients yet</h3><Button className="mt-4" onClick={() => setClientDialogOpen(true)}><Plus className="mr-2 h-4 w-4" />Add Client</Button></CardContent></Card>
      : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map(c => (
            <Card key={c.id} className="hover:border-slate-300 transition-colors cursor-pointer" onClick={() => openWorkspace(c)}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3"><div className="p-2 bg-indigo-50 rounded-lg"><Building2 className="h-5 w-5 text-indigo-600" /></div>
                    <div><CardTitle className="text-lg">{c.name}</CardTitle>{c.contact_person && <CardDescription>{c.contact_person}</CardDescription>}</div></div>
                  <Badge variant={c.is_active ? 'success' : 'secondary'}>{c.is_active ? 'Active' : 'Inactive'}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-500"><LinkIcon className="h-4 w-4" />Portal: {c.upload_url_slug}</div>
                  {c.intake_email && <div className="flex items-center gap-2 text-sm text-slate-500"><Mail className="h-4 w-4" /><span className="truncate">{c.intake_email}</span></div>}
                  <Button variant="outline" size="sm" className="w-full mt-2" data-testid={`view-client-${c.id}`} onClick={(e) => { e.stopPropagation(); openWorkspace(c); }}><Eye className="mr-2 h-4 w-4" />Open Workspace</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══ Client Workspace ═══════════════════════════════════════

const ClientWorkspace = ({ client, workspace, onBack, tenantId, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('documents');
  const [deliverables, setDeliverables] = useState([]);
  const [delLoading, setDelLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({ type: 'profit_and_loss', title: '', year: String(new Date().getFullYear()), month: String(new Date().getMonth() + 1) });
  const [uploading, setUploading] = useState(false);
  const [delFilter, setDelFilter] = useState({ type: '', year: '', month: '' });

  const loadDeliverables = useCallback(async () => {
    setDelLoading(true);
    try {
      const params = {};
      if (delFilter.type) params.deliverable_type = delFilter.type;
      if (delFilter.year) params.year = parseInt(delFilter.year);
      if (delFilter.month) params.month = parseInt(delFilter.month);
      const { data } = await intakeAPI.listDeliverables(client.id, params);
      setDeliverables(data);
    } catch { /* ignore */ }
    finally { setDelLoading(false); }
  }, [client.id, delFilter]);

  useEffect(() => { if (activeTab === 'deliverables') loadDeliverables(); }, [activeTab, loadDeliverables]);

  const handleUploadDeliverable = async () => {
    if (!uploadFile || !uploadForm.title.trim()) { toast.error('Select file and enter title'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('deliverable_type', uploadForm.type);
    fd.append('title', uploadForm.title);
    fd.append('year', uploadForm.year);
    fd.append('month', uploadForm.month);
    try {
      await intakeAPI.uploadDeliverable(client.id, fd);
      toast.success('Deliverable uploaded');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadForm({ type: 'profit_and_loss', title: '', year: String(new Date().getFullYear()), month: String(new Date().getMonth() + 1) });
      loadDeliverables();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleDeleteDeliverable = async (id) => {
    if (!window.confirm('Delete this deliverable?')) return;
    try { await intakeAPI.deleteDeliverable(client.id, id); toast.success('Deleted'); loadDeliverables(); }
    catch { toast.error('Delete failed'); }
  };

  const handleDownloadDeliverable = async (id) => {
    try { const { data } = await intakeAPI.downloadDeliverable(client.id, id); if (data.url) window.open(data.url, '_blank'); }
    catch { toast.error('Download failed'); }
  };

  const stats = workspace.stats || {};

  return (
    <div className="space-y-6" data-testid="client-workspace">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1"><h1 className="text-2xl font-semibold text-slate-900">{client.name}</h1><p className="text-slate-500">Client workspace</p></div>
        <Button variant="outline" onClick={onRefresh}><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase">Documents</p><p className="text-2xl font-bold">{stats.total_docs || 0}</p></CardContent></Card>
        <Card className="border-l-4 border-l-blue-400"><CardContent className="p-4"><p className="text-xs text-blue-600 uppercase">Pending OCR</p><p className="text-2xl font-bold">{stats.pending_ocr || 0}</p></CardContent></Card>
        <Card className="border-l-4 border-l-amber-400"><CardContent className="p-4"><p className="text-xs text-amber-600 uppercase">Needs Review</p><p className="text-2xl font-bold">{stats.needs_review || 0}</p></CardContent></Card>
        <Card className="border-l-4 border-l-emerald-400"><CardContent className="p-4"><p className="text-xs text-emerald-600 uppercase">Completed</p><p className="text-2xl font-bold">{stats.completed || 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase">Deliverables</p><p className="text-2xl font-bold">{workspace.deliverables_count || 0}</p></CardContent></Card>
      </div>

      {/* Channels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-slate-50"><CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3"><LinkIcon className="h-5 w-5 text-indigo-600" /><div><p className="text-sm font-medium">Upload Portal</p><code className="text-xs text-slate-500">{window.location.origin}/upload/{client.upload_url_slug}</code></div></div>
          <div className="flex gap-2"><Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/upload/${client.upload_url_slug}`); toast.success('Copied'); }}><Copy className="h-4 w-4" /></Button><a href={`/upload/${client.upload_url_slug}`} target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm"><ExternalLink className="h-4 w-4" /></Button></a></div>
        </CardContent></Card>
        <Card className="bg-slate-50"><CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3"><Mail className="h-5 w-5 text-indigo-600" /><div><p className="text-sm font-medium">Email Intake</p><code className="text-xs text-slate-500">{client.intake_email || 'Not configured'}</code></div></div>
          {client.intake_email && <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(client.intake_email); toast.success('Copied'); }}><Copy className="h-4 w-4" /></Button>}
        </CardContent></Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="documents"><FileText className="mr-2 h-4 w-4" />Documents ({stats.total_docs || 0})</TabsTrigger>
          <TabsTrigger value="deliverables" data-testid="tab-deliverables"><FolderOpen className="mr-2 h-4 w-4" />Deliverables</TabsTrigger>
          <TabsTrigger value="emails"><Mail className="mr-2 h-4 w-4" />Emails</TabsTrigger>
        </TabsList>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {(workspace.documents || []).length === 0 ? <div className="text-center py-12"><Receipt className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No documents yet</p></div>
              : (
                <Table>
                  <TableHeader><TableRow><TableHead>File</TableHead><TableHead>Type</TableHead><TableHead>Source</TableHead><TableHead>Status</TableHead><TableHead>Confidence</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {workspace.documents.map(d => (
                      <TableRow key={d.id}>
                        <TableCell className="font-medium text-sm truncate max-w-[200px]">{d.original_filename}</TableCell>
                        <TableCell className="capitalize text-sm">{d.document_type}</TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{d.source_channel || 'portal'}</Badge></TableCell>
                        <TableCell>
                          {d.status === 'ocr_complete' || d.status === 'approved' ? <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="mr-1 h-3 w-3" />{d.status}</Badge>
                          : d.status === 'needs_review' ? <Badge className="bg-amber-100 text-amber-700"><AlertCircle className="mr-1 h-3 w-3" />Review</Badge>
                          : d.status === 'pending' || d.status === 'processing' ? <Badge className="bg-blue-100 text-blue-700"><Clock className="mr-1 h-3 w-3" />{d.status}</Badge>
                          : <Badge variant="secondary">{d.status}</Badge>}
                        </TableCell>
                        <TableCell>{d.confidence_score != null ? <span className={`font-mono text-sm ${d.confidence_score >= 0.85 ? 'text-emerald-600' : 'text-amber-600'}`}>{(d.confidence_score * 100).toFixed(0)}%</span> : '-'}</TableCell>
                        <TableCell className="text-sm text-slate-500">{d.created_at ? new Date(d.created_at).toLocaleDateString() : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Deliverables Vault */}
        <TabsContent value="deliverables" className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-2">
              <Select value={delFilter.type || 'all_dt'} onValueChange={v => setDelFilter(p => ({...p, type: v === 'all_dt' ? '' : v}))}><SelectTrigger className="w-[180px]"><SelectValue placeholder="All Types" /></SelectTrigger><SelectContent><SelectItem value="all_dt">All Types</SelectItem>{Object.entries(DELIVERABLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
              <Select value={delFilter.year || 'all_yr'} onValueChange={v => setDelFilter(p => ({...p, year: v === 'all_yr' ? '' : v}))}><SelectTrigger className="w-[120px]"><SelectValue placeholder="Year" /></SelectTrigger><SelectContent><SelectItem value="all_yr">All Years</SelectItem>{YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
              <Select value={delFilter.month || 'all_mo'} onValueChange={v => setDelFilter(p => ({...p, month: v === 'all_mo' ? '' : v}))}><SelectTrigger className="w-[120px]"><SelectValue placeholder="Month" /></SelectTrigger><SelectContent><SelectItem value="all_mo">All Months</SelectItem>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger asChild><Button data-testid="upload-deliverable-btn"><Upload className="mr-2 h-4 w-4" />Upload Deliverable</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Upload Deliverable</DialogTitle><DialogDescription>Upload a report to the deliverables vault</DialogDescription></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2"><Label>File *</Label><Input type="file" onChange={e => setUploadFile(e.target.files[0])} data-testid="deliverable-file-input" /></div>
                  <div className="space-y-2"><Label>Title *</Label><Input placeholder="e.g., Q1 2026 P&L" value={uploadForm.title} onChange={e => setUploadForm(p => ({...p, title: e.target.value}))} data-testid="deliverable-title-input" /></div>
                  <div className="space-y-2"><Label>Type</Label><Select value={uploadForm.type} onValueChange={v => setUploadForm(p => ({...p, type: v}))}><SelectTrigger data-testid="deliverable-type-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(DELIVERABLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Year</Label><Select value={uploadForm.year} onValueChange={v => setUploadForm(p => ({...p, year: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select></div>
                    <div className="space-y-2"><Label>Month</Label><Select value={uploadForm.month} onValueChange={v => setUploadForm(p => ({...p, month: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select></div>
                  </div>
                  <div className="flex justify-end gap-3"><Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button><Button onClick={handleUploadDeliverable} disabled={uploading} data-testid="save-deliverable-btn">{uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Upload</Button></div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <Card>
            <CardContent className="p-0">
              {delLoading ? <div className="p-6 space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
              : deliverables.length === 0 ? <div className="text-center py-12"><FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No deliverables yet</p></div>
              : (
                <Table>
                  <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Period</TableHead><TableHead>File</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {deliverables.map(d => (
                      <TableRow key={d.id} data-testid={`deliverable-${d.id}`}>
                        <TableCell className="font-medium">{d.title}</TableCell>
                        <TableCell><Badge variant="outline">{DELIVERABLE_LABELS[d.deliverable_type] || d.deliverable_type}</Badge></TableCell>
                        <TableCell className="text-sm">{MONTHS[(d.month || 1) - 1]} {d.year}</TableCell>
                        <TableCell className="text-sm text-slate-500">{d.filename || '-'} {d.file_size ? `(${(d.file_size / 1024).toFixed(0)} KB)` : ''}</TableCell>
                        <TableCell>
                          <div className="flex gap-1"><Button variant="ghost" size="sm" onClick={() => handleDownloadDeliverable(d.id)} data-testid={`download-del-${d.id}`}><Download className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteDeliverable(d.id)} className="text-red-500" data-testid={`delete-del-${d.id}`}><Trash2 className="h-4 w-4" /></Button></div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Emails Tab */}
        <TabsContent value="emails" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Email Ingestions</CardTitle><CardDescription>Documents received via email</CardDescription></CardHeader>
            <CardContent className="p-0">
              {(workspace.email_ingestions || []).length === 0 ? <div className="text-center py-12"><Mail className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No emails received yet</p><p className="text-xs text-slate-400 mt-1">Send documents to {client.intake_email}</p></div>
              : (
                <Table>
                  <TableHeader><TableRow><TableHead>From</TableHead><TableHead>Subject</TableHead><TableHead>Attachments</TableHead><TableHead>Processed</TableHead><TableHead>Received</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {workspace.email_ingestions.map(e => (
                      <TableRow key={e.id}>
                        <TableCell className="text-sm">{e.from_address}</TableCell>
                        <TableCell className="text-sm truncate max-w-[200px]">{e.subject}</TableCell>
                        <TableCell>{e.attachment_count}</TableCell>
                        <TableCell><span className="text-emerald-600">{e.processed_count}</span> / <span className="text-slate-400">{e.skipped_count} skipped</span></TableCell>
                        <TableCell className="text-sm text-slate-500">{e.received_at ? new Date(e.received_at).toLocaleString() : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IntakePage;
